import { useMemo } from 'react';

import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';
import { PreOrderType, SpecialMenuCard } from 'src/gql/graphql';

import useProductQueryParams from './use-product-query-params';

type ProductFilterOverrides = {
  isOnSpecial: boolean;
  specialIds?: string[];
};

type UseSpecialFilterParamResult = {
  filterOverrides: ProductFilterOverrides | null;
  matchingSpecials: SpecialMenuCard[];
  activeFilterLabels: string[];
  specialFilter: string[];
  loading: boolean;
};

const ENDING_SOON_THRESHOLD_MS = 48 * 60 * 60 * 1000;

export type SpecialFilterKey = 'all' | 'bogo' | 'bundles' | 'delivery' | 'ending-soon' | 'everyday';

type SpecialFilterLabel =
  | 'All Sales & Offers'
  | 'BOGO'
  | 'Bundles'
  | 'Delivery Specials'
  | 'Ending Soon'
  | 'Everyday Deals';
type ComplianceFilterLabel = SpecialFilterLabel | 'All Offers' | 'Delivery Offers';

type FilterLabel = {
  label: SpecialFilterLabel;
  complianceLabel?: ComplianceFilterLabel;
};

export const CHIP_LABELS: Record<SpecialFilterKey, FilterLabel> = {
  all: { label: 'All Sales & Offers', complianceLabel: 'All Offers' },
  bogo: { label: 'BOGO', complianceLabel: 'BOGO' },
  bundles: { label: 'Bundles', complianceLabel: 'Bundles' },
  delivery: { label: 'Delivery Specials', complianceLabel: 'Delivery Offers' },
  'ending-soon': { label: 'Ending Soon', complianceLabel: 'Ending Soon' },
  everyday: { label: 'Everyday Deals', complianceLabel: 'Everyday Deals' },
} as const;

/** Resolves the display label for a special filter key, swapping to compliance wording when needed. */
export function getFilterLabel(key: SpecialFilterKey, hideSalesLanguage: boolean): string {
  const entry = CHIP_LABELS[key];
  return hideSalesLanguage && entry.complianceLabel ? entry.complianceLabel : entry.label;
}

export function isSpecialFilterKey(value: string): value is SpecialFilterKey {
  return value in CHIP_LABELS;
}

/** Whether at least one special in the given list qualifies for this filter key. */
export function specialsMatchFilterKey(key: SpecialFilterKey, specials: SpecialMenuCard[]): boolean {
  if (key === 'all') {
    return specials.length > 0;
  }
  const matcher = createSpecialMatcher(key);
  return matcher != null && specials.some(matcher);
}

export function toggleSpecialFilter(current: string[], value: string): string[] {
  if (value === 'all') {
    return current.includes('all') ? [] : ['all'];
  }
  const withoutAll = current.filter((v) => v !== 'all');
  return withoutAll.includes(value) ? withoutAll.filter((v) => v !== value) : [...withoutAll, value];
}

export function createSpecialMatcher(key: SpecialFilterKey): ((special: SpecialMenuCard) => boolean) | null {
  switch (key) {
    case 'all':
      return null;
    case 'bogo':
      return (s) => s.specialType === 'bogo';
    case 'bundles':
      return (s) => s.isBundledDiscount === true;
    case 'delivery':
      return (s) => (s.applicablePreOrderTypes ?? []).includes(PreOrderType.delivery);
    case 'ending-soon':
      return (s) => {
        if (!s.endStamp) return false;
        const endTime = new Date(s.endStamp).getTime();
        const now = Date.now();
        return endTime > now && endTime - now <= ENDING_SOON_THRESHOLD_MS;
      };
    case 'everyday':
      return (s) => s.recurringSchedule != null;
    default:
      return null;
  }
}

export function useSpecialFilterParam(): UseSpecialFilterParamResult {
  const {
    queryParams: { specialFilter },
  } = useProductQueryParams();

  const hasSpecificFilters = specialFilter.some((v) => isSpecialFilterKey(v) && v !== 'all');
  const { specials, loading } = useSpecialMenuCards({ skip: !hasSpecificFilters });

  const result = useMemo(() => {
    const activeKeys = specialFilter.filter(isSpecialFilterKey);

    if (activeKeys.length === 0) {
      return {
        filterOverrides: null,
        matchingSpecials: [] as SpecialMenuCard[],
        activeFilterLabels: [] as string[],
      };
    }

    const activeFilterLabels = activeKeys.map((k) => CHIP_LABELS[k].label);

    if (activeKeys.includes('all')) {
      return {
        filterOverrides: { isOnSpecial: true },
        matchingSpecials: [] as SpecialMenuCard[],
        activeFilterLabels,
      };
    }

    const matchers = activeKeys.map(createSpecialMatcher).filter(Boolean) as ((s: SpecialMenuCard) => boolean)[];
    const matched = specials.filter((s) => matchers.some((m) => m(s)));
    const matchedIds = matched.map((s) => s._id).filter(Boolean) as string[];

    return {
      filterOverrides: { isOnSpecial: true, specialIds: matchedIds },
      matchingSpecials: matched,
      activeFilterLabels,
    };
  }, [specialFilter, specials]);

  return { ...result, specialFilter, loading };
}
